import React, { useEffect, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography, Chip,
} from "@mui/material";
import { Cancel, RestartAlt } from "@mui/icons-material";

import { HelpBox } from "@/components/atoms";
import { useAudienceContext } from "@/context";
import { Variable, VariableGroup } from "@/types";
import {
  operatorOptions,
  demographicsCheck,
  getVariableIcon,
} from "@/utils";

interface SelectionCardProps {
  variables: Variable[];
  varGroup: VariableGroup;
  numQueryGroups: number;
  active: boolean;
  onClick: () => void;
  onRemove: (groupId: number) => void;
  disabled?: boolean;
}

const SelectionCard: React.FC<SelectionCardProps> = ({
  variables,
  varGroup,
  numQueryGroups,
  active,
  onClick,
  onRemove,
  disabled = false
}) => {
  const { dispatch } = useAudienceContext();

  const allSameDemog = demographicsCheck(varGroup.variables);

  const blockClicks = (event: React.MouseEvent) => {
    event.stopPropagation();
  }

  useEffect(() => {
    if (demographicsCheck(varGroup.variables) && varGroup.operator === 'AND') {
      dispatch({
        type: "CHANGE_GROUP_OPERATOR",
        payload: { operator: 'OR', id: varGroup.id },
      });
    }
  }, [varGroup.variables, varGroup.operator, varGroup.id, dispatch]);

  const handleRemoveGroup = () => {
    dispatch({ type: "REMOVE_VARIABLE_GROUP", payload: varGroup.id });
    onRemove(varGroup.id)
  };

  const handleChangeOperator = (operator: string) => {
    dispatch({
      type: "CHANGE_GROUP_OPERATOR",
      payload: { operator, id: varGroup.id },
    });
  };

  const handleClear = () => {
    dispatch({ type: "RESET_VARIABLE_GROUP", payload: varGroup.id });
  };

  const handleVariableDelete = (variable: Variable) => {
    const variables = varGroup.variables.filter((v) => v.tag_id !== variable.tag_id);
    dispatch({ type: "CHANGE_GROUP_VARIABLES", payload: { id: varGroup.id, variables } });
  };

  return (
    <Card
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 280,
        flexShrink: 0,
        height: "auto",
        border: active ? 1 : 0,
        borderColor: active ? "secondary.main" : undefined,
        cursor: disabled ? undefined : "pointer",
        "&:hover": {
          backgroundImage: disabled ? undefined : "linear-gradient(rgba(255,255,255,0.08), rgba(255,255,255,0.08))"
        }
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flexGrow: 1,
          pt: 1,
          pb: 0,
          "&:last-child": { pb: 0 },
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mr: "-12px" }}>
          <Typography variant="body2">Query Group {varGroup.id}</Typography>
          {!disabled && (
            <Stack direction="row" spacing={0} onClick={blockClicks}>
              <HelpBox title="Clear Query Group">
                <IconButton onClick={handleClear}>
                  <RestartAlt fontSize="small"/>
                </IconButton>
              </HelpBox>
              {numQueryGroups > 1 && (
                <HelpBox title="Remove Query Group">
                  <IconButton onClick={handleRemoveGroup}>
                    <Cancel fontSize="small"/>
                  </IconButton>
                </HelpBox>
              )}
            </Stack>
          )}
        </Box>

        <Box onClick={disabled ? undefined : blockClicks} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Match:
          </Typography>
          {operatorOptions.map((operator) => {
            const isSelected = varGroup.operator === operator.name;
            return (
              <HelpBox key={operator.name} title={operator.description}>
                <Chip
                  key={operator.name}
                  label={operator.label}
                  size="small"
                  variant={isSelected ? "filled" : "outlined"}
                  color={isSelected ? active ? "secondary" : "info" : "default"}
                  onClick={() => handleChangeOperator(operator.name as string)}
                  disabled={disabled || (allSameDemog && operator.name === 'AND')}
                />
              </HelpBox>
            )
          })}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <Typography variant="body2">
            Variables:
          </Typography>
          <Box
            sx={{
              border: 1,
              borderRadius: 1,
              borderColor: 'divider',
              minHeight: "48px",
              p: 1,
              flexGrow: 1
            }}
          >
            {varGroup.variables.map((variable) => (
              <HelpBox
                key={variable.tag_id}
                title={`${variable.tag_type} | ${variable.tag_category} | ${variable.tag_name}`}
              >
                <Chip
                  key={variable.tag_id}
                  size="small"
                  color={active ? "secondary" : "info"}
                  label={variable.tag_name}
                  icon={getVariableIcon(variable)}
                  onDelete={disabled ? undefined : (e) => {
                    e.stopPropagation();
                    handleVariableDelete(variable);
                  }}
                  sx={{ m: 0.5 }}
                  disabled={disabled}
                />
              </HelpBox>
            ))}
          </Box>
        </Box>
        {active ? (
          <Typography variant="overline" color="secondary" sx={{ textAlign: "center" }}>
            Active
          </Typography>
        ) : (
          <Box height={numQueryGroups > 1 ? 32 : 0} />
        )}

      </CardContent>
    </Card>
  );
};

export default SelectionCard;
